import { default as gqlTag } from "graphql-tag";
import {
  DependencyList,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { QueryResult, useApolloClient, useLazyQuery } from "react-apollo";
import getProducts from "src/hoc/repository/withProduct/graphql/GetProducts";
import GetProductsPriceByGroupId from "src/hoc/repository/withProductPriceByGroupId/graphql/GetProductsPriceByGroupId";
import { removeUndefinedValues } from "src/lib/utils/removeUndefinedValues";
import { IAlgoliaProduct, IProduct } from "src/types/product";

import { useAlgoliaMapperProduct } from "./useAlgoliaMapperProduct";
import {
  IAlgoliaResponse,
  IPropsQueryAlgolia,
  useAlgoliaQuery,
} from "./useAlgoliaQuery";
import { useMe } from "./useMe";

interface IProductPrice {
  productId: string;
  originalPrice: number;
  actualPrice: number;
}

// tslint:disable-next-line: no-any
const queryBuilder = (product: IProduct): any =>
  gqlTag`{ product(id: $id) { ${Object.keys(product).join(" ")} } }`;

/**
 * Set algolia product in apollo cache
 */
export const useSetProductsInApolloCache = (products: IProduct[]): void => {
  const client = useApolloClient();

  useEffect(() => {
    // Write algolia products in Apollo cache
    products.forEach((product) => {
      // Remove undefined item cause apollo is so slow
      // when we set undefined value in cache...
      const prod = removeUndefinedValues<IProduct>(product);

      // Write algolia result in apollo cache
      client.writeQuery({
        query: queryBuilder(prod),
        data: { product: prod },
        variables: { id: prod.id },
      });
    });
  }, [client, products]);
};

/**
 * Get prices by group/segment id and overwrite them in apollo cache
 * Return a boolean to force rerender when overwrite is done (maybe there is a better way to do this)
 * @param products
 */
export const useGetProductsPriceByGroupId = (products: IProduct[]): boolean => {
  const [update, forceUpdate] = useState<boolean>(false);

  const client = useApolloClient();
  const { me } = useMe();

  const [run, { data }] = useLazyQuery(GetProductsPriceByGroupId, {
    fetchPolicy: "cache-first",
  });

  /**
   * Run GetProductsPriceByGroupId when products change
   */
  useEffect(() => {
    if (!me || !products.length) {
      return;
    }

    const sort = (a: string, b: string): number =>
      parseInt(a, 10) - parseInt(b, 10);

    const ids = products.map(({ id }) => id).sort(sort);

    run({ variables: { groupId: me.groupId, ids } });
  }, [me, run, products]);

  /**
   * Overwrite products prices with productsPriceByGroup prices in apollo cache
   */
  useEffect(() => {
    if (!data) {
      return;
    }

    data?.productsPriceByGroup?.forEach(
      ({ productId, originalPrice, actualPrice }: IProductPrice) => {
        const promotion = Math.round(100 - (actualPrice * 100) / originalPrice);
        const product = {
          __typename: "Product",
          actualPrice,
          id: productId,
          originalPrice,
          promotion: promotion > 0 ? promotion + "%" : null,
        } as IProduct;

        // Write products prices in apollo cache
        client.writeQuery({
          query: queryBuilder(product),
          data: { product },
          variables: { id: product.id },
        });
      }
    );

    // force rerender
    forceUpdate((x) => !x);
  }, [client, data]);

  return update;
};

export type IApolloProducts = QueryResult<{ products: IProduct[] | null }>;

/**
 * Set products in apollo cache,
 * Get prices by group/segment id and overwrite them in apollo cache,
 * Return products from apollo cache by ids
 * @param products
 * @param ids
 */
export const useProductsFromApolloCache = (
  products: IProduct[],
  ids: string[]
): IApolloProducts => {
  useSetProductsInApolloCache(products);

  const update = useGetProductsPriceByGroupId(products);

  // Get products in apollo cache
  const [query, result] = useLazyQuery<{ products: IProduct[] | null }>(
    getProducts
  );

  useEffect(() => query({ variables: { ids } }), [ids, query, update]);

  return result;
};

interface IApolloProductsFromAlgolia extends IAlgoliaResponse<IAlgoliaProduct> {
  products: IProduct[];
  cleanProductsList: () => void;
}

/**
 * TODO: this function need to be refacto
 * @param algoliQuery
 * @param deps
 */
export const useApolloProductsFromAlgolia = (
  algoliQuery: IPropsQueryAlgolia,
  deps?: DependencyList
): IApolloProductsFromAlgolia => {
  const { offset, page = 0 } = algoliQuery;

  // Request to algolia
  const algoliaResp = useAlgoliaQuery<IAlgoliaProduct>(algoliQuery, deps);

  const prods = useAlgoliaMapperProduct(algoliaResp.hits);

  const [productsOrder, setProductsOrder] = useState<string[]>([]);
  const lastOffset = useRef<number | undefined>(offset);
  const lastPage = useRef<number>(page);

  /**
   * Set products id in state and set fake cards
   */
  useEffect(() => {
    const newIds = prods.map(({ id }) => id);

    const equalArrays = (arr1: string[], arr2: string[]): boolean =>
      arr1.length === arr2.length &&
      arr1.filter((x) => !arr2.includes(x)).length === 0;

    setProductsOrder((prodIds) => {
      if (
        (offset !== undefined &&
          lastOffset.current !== undefined &&
          offset < lastOffset.current) ||
        (offset === undefined && page < lastPage.current)
      ) {
        return prods.map(({ id }) => id).concat(prodIds);
      } else if (equalArrays(newIds, prodIds)) {
        return prodIds;
      } else {
        return prodIds.concat(prods.map(({ id }) => id));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prods]);

  useEffect(() => {
    lastOffset.current = offset;
  }, [offset]);

  useEffect(() => {
    lastPage.current = page;
  }, [page]);

  /**
   * Set and get products in apollo cache from ids
   */
  const { data, loading: loadingApollo } = useProductsFromApolloCache(
    prods,
    productsOrder
  );
  const products = data?.products ?? [];

  const loading = useRef<boolean>(true);
  const count = useRef<number>(0);

  if (algoliaResp.loading) {
    loading.current = true;
  }

  useEffect(() => {
    if (count.current === 0 && !algoliaResp.loading && !loadingApollo) {
      count.current++;
    } else if (count.current === 1 && loadingApollo) {
      count.current++;
    } else if (count.current === 2 && !algoliaResp.loading && !loadingApollo) {
      count.current = 0;
      loading.current = false;
    }
    if (
      !algoliaResp.loading &&
      (algoliaResp.stats.nbHits === 0 || prods.length === 0)
    ) {
      loading.current = false;
    }
  }, [prods, algoliaResp.loading, algoliaResp.stats.nbHits, loadingApollo]);

  const cleanProductsList = useCallback((): void => setProductsOrder([]), []);

  return {
    ...algoliaResp,
    loading: loading.current,
    products,
    cleanProductsList,
  };
};
